import React, { useState, useEffect } from 'react';
import { bool, func, string } from 'prop-types';
import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CircularProgress from '@material-ui/core/CircularProgress';

import EsmsDialog from '../../components/EsmsDialog';
import TimeZoneAutoSelect from '../../components/TimeZoneAutoSelect';
import { UserTimezoneOffset } from '../../helpers/timezones';

import SerialNumberRequirements from '../../components/SerialNumberRequirements';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as BasAgentActions from '../../actions/basAgentActions';
import * as K2ACoreActions from '../../actions/k2aCoreActions';
import * as UiActions from '../../actions/uiActions';

import classes from './NewAgentModal.module.css';
import { isEmpty, sortBy } from 'lodash';
import { Autocomplete } from '@material-ui/lab';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    serialNumber: Yup.string()
        .matches(/^[SH][2-9A-Z]{4}-[2-9A-Z]{5}-[2-9A-Z]{5}-[2-9A-Z]{5}-[2-9A-Z]{5}$/, 'Invalid Serial Number Format'),
    timeZone: Yup.object()
        .required(),
    sites: Yup.array()
        .required()
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const NewAgentModal = ({
    companyInfo,
    clearErrors,
    close,
    isLoading,
    errors,
    fetchCoreSites,
    open,
    sites,
    title,
    toggleLoading,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            name:         '',
            serialNumber: '',
            timeZone:     UserTimezoneOffset,
            sites:        []
        },
        validationSchema: ValidationSchema,
        onSubmit:         async (values, { setSubmitting }) => {
            const {
                createBasAgent
            } = props;

            setSubmitting(true);

            const agentData = {
                name:                values.name,
                serialNumber:        values.serialNumber,
                driverConfiguration: [],
                timeZone:            values.timeZone.name,
                timeZoneOffset:      values.timeZone.offSet
            };

            const linkedSites = values.sites;

            let isCreated;

            clearErrors();

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createBasAgent(companyId, agentData, linkedSites);
                }

                if (isCreated) {
                    handleClose();
                }
                else {
                    toggleLoading(false);
                    setSubmitting(false);
                }
            } catch (error) {
                toggleLoading(false);
                setSubmitting(false);
            }
        }
    });

    const [popperOpen, setPopperOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState();
    const [siteList, setSiteList] = useState([]);
    const [companyId, setCompanyId] = useState('');
    const [isSiteLoading, setIsSiteLoading] = useState(false);

    useEffect(() => {
        return () => {
            clearErrors();
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            setCompanyId(companyInfo.companyId);
            if (isEmpty(sites)) {
                setIsSiteLoading(true);
                await fetchCoreSites(companyInfo.companyId);
                setIsSiteLoading(false);
            }
        };

        if (companyInfo && companyInfo.companyId) init();

    }, [companyInfo]);

    useEffect(() => {
        if (!isEmpty(sites)) {
            const newSiteList = sites.map((s) => {
                const site = {
                    siteId:     s.id,
                    siteName:   s.name,
                    clientId:   s.client?.id,
                    clientName: s.client?.name
                };
                return site;
            });
            setSiteList(newSiteList);
        }
    }, [sites]);

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();
        close();
    };

    const handleSelectedSiteChange = (event, values) => {
        if (event.target.textContent === 'All' || event.target.value === '0') {
            if (formik.values.sites.some(item => item.id === 0)) {
                formik.setFieldValue('sites', [], true);
            } else {
                formik.setFieldValue('sites', siteList, true);
            }
        } else {
            if (values.length !== siteList.length) {
                let updatedValues = [...values];
                updatedValues = updatedValues.filter(item => item.id !== 0);
                formik.setFieldValue('sites', updatedValues, true);
            } else {
                formik.setFieldValue('sites', values, true);
            }
        }
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={formik.handleSubmit}
            onRendered={formik.handleReset}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/generalNetworkSettingsIconColor.svg" alt="settings icon" />
                        <span>New BAS Agent Settings</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            The Serial Number is unique to your Agent installation. If you have one enter it now, or wait to enter it later.
                        </p>
                        <p>
                            Choose the time zone where the Agent will be installed.
                        </p>
                        <p>
                            An agent must be linked to at least 1 site.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Agent Configuration Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="serialNumber"
                                name="serialNumber"
                                type="text"
                                value={formik.values.serialNumber}
                                label="Serial Number"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:       formik.handleBlur,
                                    endAdornment: (
                                        <InputAdornment
                                            onClick={event => {
                                                setPopperOpen(!popperOpen);
                                                setAnchorEl(event.currentTarget);
                                            }}
                                            position="end"
                                        >
                                            <i className="fas fa-question-circle" />
                                        </InputAdornment>),
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.serialNumber) && Boolean(formik.touched.serialNumber)}
                                helperText={Boolean(formik.touched.serialNumber) && formik.errors.serialNumber}
                            />
                        </FormControl>
                        <FormControl className={`${classes.inputWrapper} ${classes.timeZoneWrapper}`}>
                            <TimeZoneAutoSelect
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.timeZone}
                                error={Boolean(formik.errors.timeZone) && Boolean(formik.touched.timeZone)}
                                helperText={Boolean(formik.touched.timeZone) && formik.errors.timeZone}
                            />
                        </FormControl>
                        <FormControl className={`${classes.inputWrapper} ${classes.timeZoneWrapper}`}>
                            <Autocomplete
                                classes={{
                                    groupLabel: classes.AutoCompleteGroupLabel,
                                    option:     classes.AutoCompleteOption
                                }}
                                id="sites"
                                name="sites"
                                multiple
                                options={sortBy(siteList, ['clientName', 'siteName'])}
                                getOptionLabel={(option) => option.siteName || ''}
                                getOptionSelected={(option, value) => option && value ? option.siteId === value.siteId : ''}
                                groupBy={(option) => option.clientName}
                                value={formik.values.sites}
                                onChange={handleSelectedSiteChange}
                                onBlur={formik.handleBlur}
                                size="small"
                                required
                                renderTags={(value, getTagProps) => value.map((option, index) => {
                                    if (index < 2) {
                                        return (
                                            <Chip
                                                classes={{
                                                    root: classes.Chip
                                                }}
                                                variant="outlined"
                                                label={option.siteName}
                                                size="small"
                                                {...getTagProps({ index })}
                                            />
                                        );
                                    } else if (index === 2) {
                                        return (<span className={classes.Tag}>{` + ${value.length - 2} Selected`}</span>);
                                    } else {
                                        return null;
                                    }
                                })}
                                renderOption={(option, { selected }) => (
                                    <React.Fragment>
                                        <Checkbox
                                            value={option.id}
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.siteName}
                                    </React.Fragment>
                                )}
                                renderInput={(params) => 
                                    <TextField 
                                        {...params} 
                                        label="Select Sites" 
                                        variant="standard" 
                                        error={Boolean(formik.errors.sites) && Boolean(formik.touched.sites)}
                                        helperText={Boolean(formik.touched.sites) && formik.errors.sites}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {isSiteLoading ? <CircularProgress color="inherit" size={15} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                }
                            />
                        </FormControl>
                        <SerialNumberRequirements
                            open={popperOpen}
                            anchorEl={anchorEl}
                            placement="right"
                        />
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );

};

NewAgentModal.propTypes = {
    close: func.isRequired,
    open:  bool.isRequired,
    title: string.isRequired
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading,
        sites:       state.k2aCore.sites
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...BasAgentActions,
    ...K2ACoreActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(NewAgentModal);
